import { encryptObject } from './aesGcmEncryption';

type BridgeMessage = {
    data: string;
};

export type WindowWithBridge = Window & {
    platform?: string;
    eventsHandler?: {
        postMessage: (message: string) => void;
    };
    webkit?: {
        messageHandlers?: {
            eventsController?: {
                postMessage: (message: BridgeMessage) => void;
            };
        };
    };
};

export type PageTitleInfo = {
    showBackButton: boolean;
    rightBarButton?: {
        btnTitle: string;
        btnAction: string;
    };
    title: string;
    subTitle?: string;
};

const buildBridgeMessage = async (
    payload: Record<string, unknown>,
    encrypt = false,
): Promise<string | null> => {
    if (!encrypt) {
        return JSON.stringify(payload);
    }

    return encryptObject(payload);
};

async function publishBridgeEvent(payload: Record<string, unknown>, encrypt = false) {
       const windowWithBridge = window as WindowWithBridge;
    const platform = windowWithBridge.platform?.toLowerCase() ?? 'unknown';

    try {
        const data = await buildBridgeMessage(payload, encrypt);

        if (!data) {
            console.warn('[Bridge] Event message was empty; event was not sent.');
            return;
        }

        console.info('[Bridge] Publishing event.', { event: data });

        if (platform === 'android') {
            const handler = windowWithBridge.eventsHandler;
            if (!handler) {
                console.warn('[Bridge] Android event handler is unavailable.', { event: payload.event });
                return;
            }
            handler.postMessage(JSON.stringify({ data }));
            return;
        }
        if (platform === 'ios') {
            const handler = windowWithBridge.webkit?.messageHandlers?.eventsController;
            if (!handler) {
                console.warn('[Bridge] iOS event handler is unavailable.', { event: payload.event });
                return;
            }
            handler.postMessage({ data });
            return;
        }

        console.warn('[Bridge] Unsupported platform; event was not sent.', { event: payload.event, platform });
    } catch (error) {
        console.error('[Bridge] Failed to publish event.', { event: payload.event, platform, error });
        throw error;
    }
}

async function publishRouteChangeEvent(event: string, nextRoute: string) {
    const message = { event, nextRoute };
    await publishBridgeEvent(message);
}

async function publishApplePayEvent(event: string, orderNumber: string, orderAmount: number) {
    const message = { event, orderNumber, orderAmount };
    await publishBridgeEvent(message);
}

const ignoreBridgePublishError = () => undefined;

function toggleTopbarActions(isEnabled: boolean) {
    const event = `${isEnabled ? 'enable' : 'disable'}ToolbarActions`;
    publishBridgeEvent({ event }).catch(ignoreBridgePublishError);
}

export function publishTopBarUpdate(payload: PageTitleInfo) {
    publishBridgeEvent({ event: 'mobile-topbarUpdate', ...payload }).catch(ignoreBridgePublishError);
}

export function publishShellEvent(event: string) {
    publishBridgeEvent({ event }).catch(ignoreBridgePublishError);
}

export {
    publishBridgeEvent,
    publishApplePayEvent,
    publishRouteChangeEvent,
    toggleTopbarActions,
};
